import React from 'react';
import s from './Leads.module.scss';

const DUE_HIGH = { r: 239, g: 68, b: 68 };   // red
const DUE_MID  = { r: 255, g: 160, b: 0 };    // orange
const DUE_LOW  = { r: 124, g: 58, b: 237 };   // purple (default active color)
const DUE_NONE = '#16a34a';                    // green — all caught up

function dueColor(urgency) {
  if (urgency <= 0) return null;
  const t = urgency <= 0.5 ? urgency / 0.5 : (urgency - 0.5) / 0.5;
  const from = urgency <= 0.5 ? DUE_LOW : DUE_MID;
  const to   = urgency <= 0.5 ? DUE_MID : DUE_HIGH;
  const r = Math.round(from.r + (to.r - from.r) * t);
  const g = Math.round(from.g + (to.g - from.g) * t);
  const b = Math.round(from.b + (to.b - from.b) * t);
  return `rgb(${r},${g},${b})`;
}

const LONG_TRIP_STATUS_OPTIONS = [
  { key: 'new', label: 'New' },
  { key: 'follow_up', label: 'Follow up' },
  { key: 'converted', label: 'Converted' },
  { key: 'dropped', label: 'Dropped' },
];

const TABS = [
  { key: 'new', label: 'New', countKey: 'new' },
  { key: 'due', label: 'Due today', countKey: 'due' },
  { key: 'follow_up', label: 'Follow up', countKey: 'follow_up' },
  { key: 'converted', label: 'Converted', countKey: 'converted' },
  { key: 'dropped', label: 'Dropped', countKey: 'dropped' },
  { key: 'all', label: 'All leads', countKey: 'all' },
];

const LeadToolbar = ({
  activeTab, onTabChange, counts, dueUrgency = 0,
  searchTerm, onSearchChange,
  sourceFilter, onSourceFilterChange,
  leadTypeFilter, onLeadTypeFilterChange,
  regionFilter = '', onRegionFilterChange = () => {},
  regionLocation = '', onRegionLocationChange = () => {},
  locationCounts = [],
  longTripStatuses = ['new', 'follow_up', 'converted', 'dropped'],
  onLongTripStatusesChange = () => {},
  leadSortBy, onLeadSortByChange,
  contactByFrom, onContactByFromChange,
  contactByTo, onContactByToChange,
  onClearFilters,
  onCreateLead,
  onRefresh, refreshing,
}) => {
  return (
    <div>
      <div className={s.leadStatusToolbar}>
        <div className={s.leadStatusTabs} role="tablist" aria-label="Lead status">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`${s.leadStatusTab} ${activeTab === tab.key ? s.active : ''} ${tab.key === 'due' && dueUrgency >= 0.8 ? s.dueUrgent : ''}`}
              data-status={tab.key}
              style={tab.key === 'due' ? { color: dueUrgency > 0 ? dueColor(dueUrgency) : DUE_NONE } : undefined}
              onClick={() => onTabChange(tab.key)}
            >
              {tab.label}
              {counts[tab.countKey] !== undefined && (
                <span className={s.tabCountBadge}>{counts[tab.countKey]}</span>
              )}
            </button>
          ))}
        </div>
        <div className={s.leadStatusToolbarActions}>
          <div className={s.sourceFilterGroup}>
            <select
              id="lead-source-filter"
              className={s.sourceSelect}
              value={sourceFilter}
              onChange={(e) => onSourceFilterChange(e.target.value)}
            >
              <option value="">All Sources</option>
              <option value="bot">WhatsApp Bot</option>
              <option value="manual">Manual Entry</option>
              <option value="phone_call">Phone Call</option>
              <option value="walk_in">Walk In</option>
              <option value="marketing">Marketing (Segment Flow)</option>
              <option value="call_manager">Call Manager</option>
            </select>
          </div>
          <div className={s.searchInputGroup}>
            <span className={s.searchIcon}><i className="fa fa-search" /></span>
            <input
              type="text"
              className={s.searchInput}
              placeholder="Search by Lead ID, Customer, Phone, or Address..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {searchTerm && (
              <button className={s.clearBtn} onClick={() => onSearchChange('')}>
                <i className="fa fa-times" />
              </button>
            )}
          </div>
        </div>
      </div>
      <div className={s.leadFiltersRow}>
        <div className={s.leadFilterField}>
          <span className={s.leadFilterLabel}>Route</span>
          <button
            type="button"
            className={`${s.longTripToggle} ${activeTab === 'long_trip' ? s.longTripToggleActive : ''}`}
            aria-pressed={activeTab === 'long_trip'}
            onClick={() => onTabChange(activeTab === 'long_trip' ? 'new' : 'long_trip')}
          >
            Long trip
            {counts.long_trip !== undefined && (
              <span className={s.locationChipCount}>{counts.long_trip}</span>
            )}
          </button>
        </div>
        <div className={s.leadFilterField}>
          <label className={s.leadFilterLabel} htmlFor="lead-type-filter">Lead type</label>
          <select
            id="lead-type-filter"
            className={s.leadFilterSelect}
            value={leadTypeFilter}
            onChange={(e) => onLeadTypeFilterChange(e.target.value)}
          >
            <option value="">All types</option>
            <option value="future_booking">Future Booking</option>
            <option value="part_load_interest">Part Load Interest</option>
            <option value="general_inquiry">General Inquiry</option>
            <option value="equipment_interest">Equipment Interest</option>
            <option value="other">Other</option>
          </select>
        </div>
        {activeTab !== 'long_trip' && (
          <div className={s.leadFilterField}>
            <label className={s.leadFilterLabel} htmlFor="lead-region-filter">Region</label>
            <select
              id="lead-region-filter"
              className={s.leadFilterSelect}
              value={regionFilter}
              onChange={(e) => onRegionFilterChange(e.target.value)}
            >
              <option value="">All regions</option>
              <option value="outside">Outside region</option>
            </select>
          </div>
        )}
        <div className={s.leadFilterField}>
          <label className={s.leadFilterLabel} htmlFor="lead-sort-filter">Sort by</label>
          <select
            id="lead-sort-filter"
            className={s.leadFilterSelect}
            value={activeTab === 'all' ? 'id' : leadSortBy}
            onChange={(e) => onLeadSortByChange(e.target.value)}
            disabled={activeTab === 'all'}
          >
            {activeTab === 'all' && <option value="id">Lead ID</option>}
            <option value="recent">Recent leads</option>
            <option value="contact_by">Contact by</option>
          </select>
        </div>
        <div className={s.leadFilterField}>
          <span className={s.leadFilterLabel}>Contact by</span>
          <div className={s.leadDateRange}>
            <input
              type="date"
              className={s.leadDateInput}
              value={contactByFrom}
              onChange={(e) => onContactByFromChange(e.target.value)}
              aria-label="Contact by from"
            />
            <span className={s.leadDateSep}>to</span>
            <input
              type="date"
              className={s.leadDateInput}
              value={contactByTo}
              onChange={(e) => onContactByToChange(e.target.value)}
              aria-label="Contact by to"
            />
          </div>
        </div>
        {(leadTypeFilter || regionFilter || regionLocation || contactByFrom || contactByTo
          || (activeTab === 'long_trip' && longTripStatuses.length < LONG_TRIP_STATUS_OPTIONS.length)) && (
          <button type="button" className={s.leadClearFiltersBtn} onClick={onClearFilters}>
            <i className="fa fa-times" /> Clear
          </button>
        )}
      </div>
      {activeTab === 'long_trip' && (
        <div className={s.statusFilterRow} role="group" aria-label="Long-trip lead statuses">
          <span className={s.leadFilterLabel}>Status</span>
          {LONG_TRIP_STATUS_OPTIONS.map((option) => {
            const checked = longTripStatuses.includes(option.key);
            return (
              <label key={option.key} className={`${s.statusFilterChip} ${checked ? s.statusFilterChipActive : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? longTripStatuses.filter((key) => key !== option.key)
                      : [...longTripStatuses, option.key];
                    if (next.length === 0) return;
                    onLongTripStatusesChange(next);
                  }}
                />
                {option.label}
              </label>
            );
          })}
        </div>
      )}
      {activeTab === 'long_trip' && (
        <div className={s.locationChipRow} role="group" aria-label="Long-trip locations">
          <button
            type="button"
            className={`${s.locationChip} ${!regionLocation ? s.locationChipActive : ''}`}
            onClick={() => onRegionLocationChange('')}
          >
            All locations
            <span className={s.locationChipCount}>
              {locationCounts.reduce((sum, item) => sum + (item.count || 0), 0)}
            </span>
          </button>
          {locationCounts.map((item) => (
            <button
              key={item.location}
              type="button"
              className={`${s.locationChip} ${regionLocation.toLowerCase() === String(item.location).toLowerCase() ? s.locationChipActive : ''}`}
              onClick={() => onRegionLocationChange(
                regionLocation.toLowerCase() === String(item.location).toLowerCase() ? '' : item.location
              )}
            >
              {item.location}
              <span className={s.locationChipCount}>{item.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadToolbar;
